import React, { useState, useEffect } from "react";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, db, analytics, Timestamp } from "./firebase-config";
import {
  addDoc,
  setDoc,
  collection,
  doc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { Link, useLocation } from "react-router-dom";
import { Country } from "country-state-city";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from "libphonenumber-js";
import { useTranslation } from "react-i18next";
import { ColorRing } from "react-loader-spinner";
import myLogo from "./public/ProRecruitBusinessLogoTransparent2.png";
import { useToast } from "./components/Alerts";
import { logEvent } from "firebase/analytics";
import i18next from "i18next";
import TawkToChat from "./components/TawkToChat";
import teamAnimation from "./public/Handshake.json";
import Lottie from "lottie-react";

function generateUniqueId16(companyName) {
  if (!companyName) return generateRandomId(16);
  const sanitizedName = companyName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .substring(0, 10);
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const uniqueId = `${sanitizedName
    .padEnd(5, "0")
    .substring(0, 5)}${timestamp.slice(-6)}${random}`;
  return uniqueId.length > 16 ? uniqueId.substring(0, 16) : uniqueId;
}

function generateRandomId(length) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

function SignUpPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [passwordMatchMessage, setPasswordMatchMessage] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyCountry, setCompanyCountry] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState("");
  const [referer, setReferer] = useState("");
  const [affiliateID, setAffiliateID] = useState("");

  const { addToast } = useToast();
  const location = useLocation();
  const { t } = useTranslation("SignUpPage");
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language);

  // Styling Constants
  const inputClassName =
    "block w-full px-4 py-3 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#A47713] focus:border-transparent transition duration-200 ease-in-out";
  const labelClassName = "block text-sm font-semibold text-slate-700 mb-1";

  const toggleLanguage = () => {
    const newLang = currentLanguage === "en" ? "fr" : "en";
    i18next.changeLanguage(newLang);
    setCurrentLanguage(newLang);
  };

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.userLanguage;
    const languageCode = browserLanguage.split("-")[0];
    if (
      languageCode !== i18next.language &&
      (languageCode === "en" || languageCode === "fr")
    ) {
      i18next.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
    }
  }, []);

  useEffect(() => {
    document.title = t("signUpTitle") + " - ProRecruit";
    return () => {
      document.title = "ProRecruit";
    };
  }, [t]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const affiliateCode = urlParams.get("affiliate");
    const refererCode = urlParams.get("referer");
    const referenceCode = urlParams.get("reference");
    logEvent(analytics, "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      affiliateCode: affiliateCode,
      refererCode: refererCode,
      referenceCode: referenceCode,
    });
    if (refererCode) setReferer(refererCode);
    if (referenceCode) setReference(referenceCode);
    if (affiliateCode) setAffiliateID(affiliateCode);
  }, []);

  useEffect(() => {
    if (password && confirmPassword && password !== confirmPassword) {
      setPasswordMatchMessage(t("passwordMismatch"));
    } else {
      setPasswordMatchMessage("");
    }
  }, [password, confirmPassword, t]);

  const showSuccessToast = (m) => addToast({ type: "success", message: m, dismissible: true });
  const showErrorToast = (m) => addToast({ type: "error", message: m, dismissible: true });

  const handleCountryChange = (event) => {
    const countryCode = event.target.value;
    setCompanyCountry(Country.getCountryByCode(countryCode));
  };

  const handlePhoneNumberChange = (e) => {
    const inputValue = e.target.value;
    if (!/^[0-9+ -]*$/.test(inputValue)) return;
    setPhoneNumber(inputValue);
  };

  // Validation functions for multi-step
  const validateStep1 = () => {
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      showErrorToast(t("fillAllFields"));
      return false;
    }
    if (password !== confirmPassword) {
      showErrorToast(t("passwordMismatch"));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showErrorToast(t("invalidEmail"));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!companyName || !companyAddress || !companyCity || !companyCountry) {
      showErrorToast(t("fillAllFields"));
      return false;
    }
    const number = parsePhoneNumberFromString(phoneNumber);
    if (!phoneNumber) {
      setPhoneNumberError(t("phoneNumberRequired"));
      return false;
    }
    if (!number || !isValidPhoneNumber(`${number.number}`)) {
      setPhoneNumberError(t("invalidPhoneNumber"));
      return false;
    }
    setPhoneNumberError("");
    return true;
  };

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);

    // Final Validation check just in case
    if (!validateStep1() || !validateStep2()) return;

    setLoading(true);
    const number = parsePhoneNumberFromString(phoneNumber);
    const formattedNumber = number.format("E.164");

    try {
      console.log("Creating user with email: ", email, auth.config);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const tenantID = generateUniqueId16(companyName);

      const userData = {
        firstName, lastName, email, phoneNumber: formattedNumber,
        displayName: `${firstName} ${lastName}`,
        companyName, companyAddress, companyCity, companyCountry: companyCountry.name,
        language: currentLanguage, reference, audience: "recruiter",
        affiliateID, referer, tenantId: tenantID,
        originalTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID,
        createdDate: serverTimestamp(), modifiedDate: serverTimestamp(),
        basicLicense: 0, proLicense: reference === "tuoerkl" ? 2 : 0, fullLicense: 0,
        trialLicense: 1, canUpdateCompany: true, canCreateAssessment: true,
        canAddJobOffer: true, hasPurchasedLicense: false, lastActivityDate: serverTimestamp(),
      };

      const leadData = {
        jobTitle: "", segment: "free-trial", company: companyName,
        language: currentLanguage, firstName, lastName, email,
        country: companyCountry.name, hsEmailDomain: email.split("@")[1],
        phone: formattedNumber, audience: "recruiter", website: "",
        city: companyCity, address: companyAddress, postalCode: "",
        isActiveTrial: false, lastEmailOpenDate: null, industry: "",
        candidatesProcessed: 0, numberOfEmployees: null, hsLeadStatus: "",
        createdDate: serverTimestamp(), hasPostedJob: false, stateRegion: "", totalRevenue: null,
      };

      const companyData = {
        name: companyName, address: companyAddress, city: companyCity,
        country: companyCountry, primaryContact: email, tenantId: tenantID,
        originalTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID,
        createdDate: serverTimestamp(), modifiedDate: serverTimestamp(),
        reference, affiliateCode: affiliateID, referer, language: currentLanguage,
        proLicense: reference === "tuoerkl" ? 2 : 0, growthLicense: reference === "tuoerkl" ? 2 : 0,
        fullLicense: 0, basicLicense: 0, trialLicense: 1, candidatesProcessed: 0,
        hasPurchasedLicense: false, lastActivityDate: serverTimestamp(),
        hasUsedFreeTrial: false, hasUsedPaidLicense: false,
      };

      const marketingData = {
        emailStatus: {
          welcomeSent: true, daily1Sent: false, daily2Sent: false, daily3Sent: false,
          daily4Sent: false, daily5Sent: false, daily6Sent: false, daily7Sent: false,
          featureHighlight1Sent: false, featureHighlight2Sent: false, usageTipSent: false,
          approachingLimitSent: false, limitReachedSent: false, reactivationSent: false,
        },
      };

      const DEFAULT_LANGUAGE = "en";
      const emailType = `welcome-email`;
      let templateId = null;
      let templateDocData = null;
      const templateDocRef = doc(db, "sendgrid-template-id", emailType);
      const templateDoc = await getDoc(templateDocRef);

      if (templateDoc.exists()) {
        templateDocData = templateDoc.data();
        templateId = templateDocData[currentLanguage] || templateDocData[DEFAULT_LANGUAGE];
      }

      const templateData = { firstName, lastName, companyName };
      const trackingSettings = {
        clickTracking: { enable: true, enableText: true },
        openTracking: { enable: true },
        subscriptionTracking: { enable: false },
        ganalytics: {
          enable: true, utmSource: "sendgrid", utmMedium: "email",
          utmCampaign: "Onboarding-Sequence", utmTerm: emailType,
          utmContent: templateDocData?.[`subject-${currentLanguage}`] || "Welcome to ProRecruit",
        },
      };

      const emailQueueData = {
        to: email, template: true, templateId: templateId, templateData: templateData,
        categories: ["Marketing", "Onboarding", "Welcome-email"],
        trackingSettings: trackingSettings,
        customArgs: { companyId: tenantID, emailType: emailType, language: currentLanguage },
        asm: { group_id: 24255, groups_to_display: [24255, 27845] },
        createdAt: Timestamp.now(),
      };

      await Promise.all([
        setDoc(doc(db, "users", user.email), { userData }),
        setDoc(doc(db, "company", tenantID), { companyData, marketingData }),
        setDoc(doc(db, "leads", user.email), { leadData }),
        addDoc(collection(db, "send-grid-emails"), emailQueueData),
      ]);

      const idToken = await user.getIdToken();
      const verificationEmailResponse = await fetch(`${process.env.REACT_APP_FIREBASE_SET_CUSTOM_CLAIMS_URL}/sendVerificationEmail`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          uid: user.uid, audience: "recruiter", tenantId: tenantID, language: currentLanguage,
          canUpdateCompany: true, canCreateAssessment: true, canAddJobOffer: true,
          hasPurchasedLicense: false, originalTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID.replace(/['"]/g, '').replace(/\r/g, '').replace(/\n/g, ''),
          signupSource: "web_form",
        }),
      });
      if (!verificationEmailResponse.ok) {
        console.warn("Failed to send verification email:", await verificationEmailResponse.json());
      }
      const claimsResponse = await fetch(`${process.env.REACT_APP_FIREBASE_SET_CUSTOM_CLAIMS_URL}/setCustomClaims`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          uid: user.uid, audience: "recruiter", tenantId: tenantID, language: currentLanguage,
          canUpdateCompany: true, canCreateAssessment: true, canAddJobOffer: true,
          hasPurchasedLicense: false, originalTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID.replace(/['"]/g, '').replace(/\r/g, '').replace(/\n/g, ''),
          signupSource: "web_form",
        }),
      });

      if (!claimsResponse.ok) {
        console.warn("Failed to set user role:", await claimsResponse.json());
      }

      logEvent(analytics, "sign_up", {
        method: "email", tenant_id: tenantID, company_name: companyName,
        company_address: companyAddress, company_city: companyCity,
        company_country: companyCountry.name, affiliateID: affiliateID,
        referer: referer, reference: reference,
      });
      logEvent(analytics, "qualify_lead", { currency: "XOF", value: 100000 });

      signOut(auth);
      showSuccessToast(t("verificationEmailSent"));
      setVerificationSent(true);
    } catch (error) {
      console.error("Signup error:", error);
      let errorMessage = "An unexpected error occurred.";
      switch (error.code) {
        case "auth/email-already-in-use": errorMessage = t("emailInUse"); break;
        case "auth/phone-number-already-exists": errorMessage = t("phoneInUse"); break;
        case "auth/invalid-email": errorMessage = t("invalidEmail"); break;
        case "auth/weak-password": errorMessage = t("weakPassword"); break;
        default: break;
      }
      showErrorToast(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans smooth-scroll flex flex-col lg:grid lg:grid-cols-12 relative">
      {/* Inline styles for custom animations and textures */}
      <style>{`
        .smooth-scroll { scroll-behavior: smooth; }
        .bg-texture {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Language Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={toggleLanguage}
          className="inline-flex items-center justify-center rounded-full px-4 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-100 hover:text-[#A47713] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A47713] focus-visible:ring-offset-2 transition-all duration-300 shadow-sm"
        >
          <span className="mr-2">{currentLanguage === "en" ? "🇫🇷" : "🇬🇧"}</span>
          {currentLanguage === "en" ? "Français" : "English"}
        </button>
      </div>

      {/* Left Sidebar (Visible on LG screens) */}
      <aside className="hidden lg:col-span-5 lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden bg-texture">
        {/* Subtle Parallax Background Effect via absolute div */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/50 to-transparent pointer-events-none"></div>

        <div className="relative z-10 flex flex-col justify-between h-full">
          <div>
            <img className="h-10 w-auto mb-16" src={myLogo} alt="ProRecruit" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              {t("sidebarHeading")}
            </h1>
            <p className="mt-6 text-lg text-slate-300">
              {t("sidebarSubheading")}
            </p>

            <ul className="mt-10 space-y-6">
              {[t("feature1"), t("feature2"), t("feature3")].map((feature, idx) => (
                <li key={idx} className="flex items-start animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-[#A47713] flex-shrink-0 mr-4 mt-0.5">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                  <span className="text-slate-200 font-medium">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <blockquote className="mt-16 bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm">
            <p className="text-lg font-medium italic text-slate-200">
              "{t("testimonialQuote")}"
            </p>
            <footer className="mt-4 text-sm font-semibold text-[#A47713]">
              {t("testimonialAuthor")}
            </footer>
          </blockquote>
        </div>
      </aside>

      {/* Right Content Area (Form) */}
      <main className="lg:col-span-7 flex flex-col justify-center py-12 px-6 sm:px-10 lg:px-20 xl:px-32 bg-white relative z-10">
        <div className="mx-auto w-full max-w-xl">
          {verificationSent ? (
            <div className="text-center animate-fade-in">
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
                <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-3xl font-extrabold text-slate-900 mb-4">{t("checkYourEmail")}</h3>
              <p className="text-lg text-slate-600 mb-8">{t("verificationEmailSent")}</p>
              <Link
                to="/signin"
                state={{ from: location }}
                className="inline-flex justify-center w-full py-3 px-6 border border-transparent text-base font-medium rounded-full text-white bg-[#A47713] hover:bg-[#8A6310] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713] transition-all transform hover:-translate-y-0.5 shadow-lg"
              >
                {t("goToLogin")}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-10 text-center lg:text-left">
                {/* Mobile logo fallback */}
                <img className="h-10 w-auto mx-auto lg:hidden mb-6" src={myLogo} alt="ProRecruit" />
                <h2 className="text-3xl font-extrabold text-slate-900">{t("createAccountTitle")}</h2>
                <p className="mt-3 text-base text-slate-600">
                  {t("or")}{" "}
                  <Link to="/signin" state={{ from: location }} className="font-semibold text-[#A47713] hover:text-[#8A6310] transition-colors">
                    {t("signInAccount")}
                  </Link>
                </p>
              </div>

              {error && <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-600 text-center animate-fade-in">{error}</div>}

              <form className="space-y-8" onSubmit={handleSignUp}>
                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <p className="text-sm font-bold tracking-wide text-slate-500 uppercase">
                      {t("stepXofY", { current: currentStep, total: totalSteps })}
                    </p>
                    <span className="text-xs font-semibold text-[#A47713]">
                      {currentStep === 1 ? t("step1Title") : currentStep === 2 ? t("step2Title") : t("step3Title")}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-[#A47713] h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="min-h-[320px] relative">
                  {/* STEP 1: Account Basics */}
                  {currentStep === 1 && (
                    <div className="space-y-6 animate-fade-in absolute w-full">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="firstName" className={labelClassName}>{t("firstName")}</label>
                          <input id="firstName" type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClassName} />
                        </div>
                        <div>
                          <label htmlFor="lastName" className={labelClassName}>{t("lastName")}</label>
                          <input id="lastName" type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClassName} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className={labelClassName}>{t("email")}</label>
                        <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClassName} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="password" className={labelClassName}>{t("password")}</label>
                          <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClassName} />
                        </div>
                        <div>
                          <label htmlFor="confirmPassword" className={labelClassName}>{t("confirmPassword")}</label>
                          <input id="confirmPassword" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClassName} />
                          {passwordMatchMessage && <p className="mt-2 text-xs text-red-500 font-medium">{passwordMatchMessage}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Company Details */}
                  {currentStep === 2 && (
                    <div className="space-y-6 animate-fade-in absolute w-full">
                      <div>
                        <label htmlFor="companyName" className={labelClassName}>{t("companyName")}</label>
                        <input id="companyName" type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputClassName} />
                      </div>
                      <div>
                        <label htmlFor="phoneNumber" className={labelClassName}>{t("phoneNumber")}</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            {companyCountry && <span className="text-lg">{companyCountry.flag}</span>}
                          </div>
                          <input type="tel" id="phoneNumber" className={`${inputClassName} ${companyCountry ? 'pl-12' : ''}`} placeholder="+228 99 99 99 99" value={phoneNumber} onChange={handlePhoneNumberChange} />
                        </div>
                        {phoneNumberError && <p className="mt-2 text-xs text-red-500 font-medium">{phoneNumberError}</p>}
                      </div>
                      <div>
                        <label htmlFor="companyAddress" className={labelClassName}>{t("companyAddress")}</label>
                        <input id="companyAddress" type="text" required value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} className={inputClassName} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="companyCity" className={labelClassName}>{t("companyCity")}</label>
                          <input id="companyCity" type="text" required value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} className={inputClassName} />
                        </div>
                        <div>
                          <label htmlFor="companyCountry" className={labelClassName}>{t("companyCountry")}</label>
                          <select id="companyCountry" required value={companyCountry ? companyCountry.isoCode : ""} onChange={handleCountryChange} className={inputClassName}>
                            <option value="">{t("selectCountry")}</option>
                            {Country.getAllCountries().map((country) => (
                              <option key={country.isoCode} value={country.isoCode}>{country.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Final / Referrals */}
                  {currentStep === 3 && (
                    <div className="space-y-6 animate-fade-in absolute w-full">
                      <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl mb-6">
                        <h4 className="text-slate-800 font-semibold mb-2">Almost there, {firstName}!</h4>
                        <p className="text-slate-600 text-sm">Have a referral or affiliate code? Enter it below. If not, just click complete!</p>
                      </div>
                      <div>
                        <label htmlFor="reference" className={labelClassName}>{t("referenceCode")}</label>
                        <input id="reference" type="text" value={reference} onChange={(e) => setReference(e.target.value)} className={inputClassName} placeholder="Optional code" />
                      </div>
                      {/* Can add more optional fields here if needed */}
                    </div>
                  )}
                </div>

                {/* Form Navigation Actions */}
                <div className="flex justify-between items-center pt-8 border-t border-slate-100 mt-8">
                  <button
                    type="button"
                    onClick={prevStep}
                    className={`inline-flex items-center px-6 py-3 border border-slate-300 shadow-sm text-sm font-semibold rounded-full text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713] transition-all duration-200 ${currentStep === 1 ? 'invisible' : 'visible'}`}
                  >
                    {t("backButton")}
                  </button>
                  {/* STEP 3: Final / Referrals */}
                  {currentStep === 3 && (
                    <div className="space-y-6 animate-fade-in absolute w-full flex flex-col items-center">

                      {/* The Lottie Animation */}
                      <div className="w-48 h-48 -mt-6 mb-2">
                        <Lottie animationData={teamAnimation} loop={true} />
                      </div>

                      <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl mb-6 w-full text-center">
                        <h4 className="text-slate-800 font-semibold mb-2 text-lg">Almost there, {firstName || 'friend'}!</h4>
                        <p className="text-slate-600 text-sm">Have a referral or affiliate code? Enter it below. If not, just click complete!</p>
                      </div>

                      <div className="w-full">
                        <label htmlFor="reference" className={labelClassName}>{t("referenceCode")}</label>
                        <input id="reference" type="text" value={reference} onChange={(e) => setReference(e.target.value)} className={inputClassName} placeholder="Optional code" />
                      </div>
                    </div>
                  )}

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="inline-flex items-center px-8 py-3 border border-transparent text-sm font-semibold rounded-full shadow-md text-white bg-[#A47713] hover:bg-[#8A6310] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713] transition-all transform hover:-translate-y-0.5"
                    >
                      {t("nextButton")}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center items-center px-8 py-3 border border-transparent text-sm font-bold rounded-full shadow-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all transform hover:-translate-y-0.5 hover:scale-105 active:scale-95 disabled:opacity-70 disabled:pointer-events-none"
                    >
                      {loading ? (
                        <ColorRing visible={true} height="24" width="24" ariaLabel="color-ring-loading" wrapperStyle={{}} wrapperClass="color-ring-wrapper" colors={['#fff', '#fff', '#fff', '#fff', '#fff']} />
                      ) : (
                        t("signUpButton")
                      )}
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </main>
      <TawkToChat />
    </div>
  );
}

export default SignUpPage;